import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, type Io } from '../output.js';

interface CommonOptions {
  dir?: string;
  json?: boolean;
  adminUrl?: string;
}

function setup(options: CommonOptions): { io: Io; client: ApiClient; slug: string } {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');
  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;
  let slug: string;
  try {
    slug = readAppConfig(dir).slug;
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }
  return { io, client: new ApiClient(resolved.adminUrl, resolved.profile.token), slug };
}

function whenish(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'seconds' in (value as Record<string, unknown>)) {
    const seconds = Number((value as { seconds: unknown }).seconds);
    if (Number.isFinite(seconds)) return new Date(seconds * 1000).toISOString();
  }
  return '';
}

export async function runDeploys(options: CommonOptions & { limit?: string }): Promise<void> {
  const { io, client, slug } = setup(options);
  const limit = Math.max(1, Math.min(Number(options.limit) || 20, 50));
  try {
    const res = await client.get<{ slug: string; deploys: Array<Record<string, unknown>> }>(
      `/builder-api/apps/${slug}/deploys?limit=${limit}`,
    );
    if (io.json) {
      emitJson({ ok: true, slug, deploys: res.deploys });
      return;
    }
    if (res.deploys.length === 0) {
      human(io, `No deploys yet for ${slug}.`);
      return;
    }
    for (const d of res.deploys) {
      const status = String(d.status ?? 'UNKNOWN');
      const colored =
        status === 'SUCCESS' ? pc.green(status) : status === 'FAILURE' ? pc.red(status) : pc.yellow(status);
      human(
        io,
        `${colored.padEnd(io.json ? 0 : 18)}  ${whenish(d.createTime)}  ${String(d.commitSha ?? '').slice(0, 8)}  ${String(d.id ?? '')}`,
      );
    }
  } catch (err) {
    fail(io, (err as Error).message, 1, 'API_ERROR');
  }
}

export async function runLogs(options: CommonOptions & { limit?: string }): Promise<void> {
  const { io, client, slug } = setup(options);
  const limit = Math.max(1, Math.min(Number(options.limit) || 100, 100));
  try {
    const res = await client.get<{
      slug: string;
      entries: Array<{ timestamp?: unknown; severity?: unknown; message: string }>;
    }>(`/builder-api/apps/${slug}/logs`);
    const entries = res.entries.slice(0, limit);
    if (io.json) {
      emitJson({ ok: true, slug, entries });
      return;
    }
    if (entries.length === 0) {
      human(io, `No recent logs for ${slug}.`);
      return;
    }
    for (const e of entries) {
      const sev = String(e.severity ?? '');
      const sevText = sev === 'ERROR' ? pc.red(sev) : sev === 'WARNING' ? pc.yellow(sev) : pc.dim(sev);
      human(io, `${pc.dim(whenish(e.timestamp))} ${sevText} ${e.message}`);
    }
  } catch (err) {
    fail(io, (err as Error).message, 1, 'API_ERROR');
  }
}

export async function runRedeploy(options: CommonOptions): Promise<void> {
  const { io, client, slug } = setup(options);
  try {
    const res = await client.post<{ ok: boolean; buildId: string }>(
      `/builder-api/apps/${slug}/redeploy`,
    );
    if (io.json) {
      emitJson({ ok: true, slug, buildId: res.buildId });
      return;
    }
    human(io, `${pc.green('✔')} Redeploy triggered for ${slug} (build ${res.buildId}).`);
    human(io, `  Watch progress with \`ghosty deploys\`.`);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'API_ERROR');
  }
}
